import React, { useState, useEffect } from 'react';
import { apiService } from '@/lib/api';
import { QueueStatus } from '@/types/queue-monitoring';
import QueueStatusCard from '@/components/monitoring/QueueStatusCard';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const QueueMonitoringPage: React.FC = () => {
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await apiService.get('/monitoring/queues-status');
      if (response.success) {
        setQueues(response.data);
        setLastUpdated(new Date());
      } else {
        throw new Error(response.error || 'Falha ao buscar dados de monitoramento.');
      }
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro de rede.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); // Busca inicial
    const intervalId = setInterval(fetchData, 30000); // Atualiza a cada 30 segundos

    return () => clearInterval(intervalId); // Limpa o intervalo ao desmontar
  }, []);

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Monitoramento de Filas de Disparo</h1>
          <p className="text-gray-500">O status é atualizado automaticamente a cada 30 segundos.</p>
        </div>
        <div className='flex items-center gap-4'>
            {lastUpdated && <p className="text-sm text-gray-400">Última atualização: {lastUpdated.toLocaleTimeString()}</p>}
            <Button onClick={fetchData} disabled={loading} variant="outline" size="icon">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
        </div>
      </div>

      {loading && queues.length === 0 && <AtomicLoadingScreen />}
      
      {error && (
        <div className="flex flex-col items-center justify-center text-red-500 bg-red-100 p-6 rounded-lg">
          <AlertCircle className="h-12 w-12 mb-4" />
          <p className="text-lg font-semibold">Erro ao carregar dados</p>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && queues.length === 0 && (
        <div className="text-center text-gray-500 py-16">
          <p>Nenhuma fila de cliente ativa encontrada.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {queues.map((queue) => (
          <QueueStatusCard key={queue.clientId} queue={queue} />
        ))}
      </div>
    </div>
  );
};

export default QueueMonitoringPage; 